import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { makeStyles, Grid, Typography, IconButton, Checkbox, FormControlLabel, Switch } from "@material-ui/core";
import productPageStyle from "features/products/styles";
import { useTranslation } from "react-i18next";
import { Clear, FiberManualRecord } from "@material-ui/icons";
import { DateTime, DynamicInput } from "@totalsoft/rocket-webapp-components-deprecated";
import { scheduleTypeCellParams } from "features/products/constants";

const useStyles = makeStyles(productPageStyle);

const CellParamConfiguration = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const {
    param,
    onEdit,
    onClear,
    cellParamDefinitions,
    currencyCode,
    onCheckBoxChange,
    capitalPercentAutoCalculation,
  } = props;

  const paramDef = useMemo(
    () => cellParamDefinitions.find((x) => x.cellParamName === param.scheduleTypeCellParamCode),
    [param.scheduleTypeCellParamCode, cellParamDefinitions]
  );

  const paramEditType = useMemo(() => {
    if (!paramDef?.cellParamTypeId) return null;
    switch (paramDef.cellParamTypeId) {
      case -1724:
        return "BOOL";
      case -1726:
      case -1727:
      case -1729:
      case -1829:
        return "NUM";
      case -1728:
        return "DATE";
    }
  }, [paramDef?.cellParamTypeId]);

  const decimalScale = useMemo(() => {
    if (!paramDef?.cellParamTypeId) return { decimalScale: 4 };
    switch (paramDef?.cellParamTypeId) {
      case -1724:
        return {};
      case -1726:
        return { decimalScale: 2 };
      case -1727:
        return { decimalScale: 4 };
      case -1729 || -1829:
        return { decimalScale: 6 };
    }
  }, [paramDef?.cellParamTypeId]);

  const optionLabelType = useMemo(() => {
    if (!paramDef || !paramEditType || paramEditType !== "NUM") {
      return null;
    }

    if (paramDef?.cellParamTypeName === "Amount") {
      return { startAdornment: currencyCode };
    }

    if (paramDef?.cellParamTypeName.includes("Procent")) {
      return { startAdornment: t("General.Percent") };
    }
    return null;
  }, [t, currencyCode, paramEditType, paramDef]);

  const parseValue = useCallback((param, paramDef) => {
    switch (paramDef.cellParamTypeId) {
      case -1724:
        return param?.value === "true";
      case -1726:
      case -1727:
      case -1729:
      case -1829:
        return Number(param?.value);
      case -1728:
        return new Date(param?.value);
    }
  }, []);

  const handleEdit = useCallback((value) => onEdit(param, paramDef, value), [onEdit, param, paramDef]);

  const handleClear = useCallback(() => onClear(param), [onClear, param]);

  const isCapitalPercentParam = param.scheduleTypeCellParamCode === scheduleTypeCellParams.capitalPercentCode;

  const onCheckboxPropertyChange = useCallback(
    (_, inputValue) => {
      handleEdit(inputValue);
    },
    [handleEdit]
  );

  return (
    <Grid item key={paramDef.scheduleTypeCellParamId} xs={12} container alignItems="center">
      <Grid item xs={2} container alignItems="center" justifyContent="space-between">
        <Typography variant="caption">{t("EditProduct.Option")}</Typography>
        <span className={classes.marginRight}>
          <FiberManualRecord className={classes.optionDot} style={{ color: paramDef.color }} />
        </span>
      </Grid>
      <Grid item xs={true}>
        <div className={classes.option}>
          <Grid container direction="row" alignItems="center">
            <Grid item xs={5} className={classes.padding5}>
              <Typography variant="caption">{paramDef.cellParamDisplayName}</Typography>
            </Grid>
            {isCapitalPercentParam && (
              <Grid item xs={2}>
                <Checkbox size="small" checked={capitalPercentAutoCalculation} onChange={onCheckBoxChange} />
              </Grid>
            )}
            <Grid item xs className={classes.padding5}>
              {paramEditType === "DATE" ? (
                <DateTime autoFocus disableToolbar={false} onChange={handleEdit} value={parseValue(param, paramDef)} />
              ) : paramEditType === "BOOL" ? (
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      autoFocus
                      disabled={isCapitalPercentParam && capitalPercentAutoCalculation}
                      checked={parseValue(param, paramDef)}
                      onChange={onCheckboxPropertyChange}
                    />
                  }
                />
              ) : (
                <DynamicInput
                  size="small"
                  autoFocus
                  disabled={isCapitalPercentParam && capitalPercentAutoCalculation}
                  controlType={paramEditType}
                  onChange={handleEdit}
                  value={parseValue(param, paramDef)}
                  inputProps={{
                    ...decimalScale,
                  }}
                  {...optionLabelType}
                />
              )}
            </Grid>
            <Grid item xs={2} container justifyContent="flex-end">
              <IconButton className={classes.padding4} onClick={handleClear}>
                <Clear fontSize="small" />
              </IconButton>
            </Grid>
          </Grid>
        </div>
      </Grid>
    </Grid>
  );
};

CellParamConfiguration.propTypes = {
  param: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  cellParamDefinitions: PropTypes.array.isRequired,
  currencyCode: PropTypes.string.isRequired,
  onCheckBoxChange: PropTypes.func.isRequired,
  capitalPercentAutoCalculation: PropTypes.bool.isRequired,
};

export default CellParamConfiguration;
